import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	EventEmitter,
	HostBinding,
	Input,
	OnChanges,
	OnDestroy,
	OnInit,
	Output,
	SimpleChange,
	ViewEncapsulation,
} from "@angular/core";
import { select, Store } from "@ngrx/store";
import { ThumbnailVariant } from "@obg/gaming.config";
import { InteractionIcon, ThumbnailSize } from "@obg/gaming.config/src/gaming-ui/custom-lobbies/components-props";
import {
	CORE_ROUTE,
	CustomerComplianceService,
	UiInteraction,
	UiInteractionAction,
	Jurisdiction,
	JurisdictionSelector,
} from "@obg/ngx.core";
import { CurrencyService, DeviceConfigService } from "@obg/ngx.global";
import { coerceBoolean } from "@obg/ngx.global.evo";
import { RouteAction, RouteLink } from "@obg/ngx.router";
import { ignoreNil } from "@obg/rxjs";
import { head, isEmpty, merge as lodashMerge } from "lodash-es";
import { merge, Observable, Subscription } from "rxjs";
import { map, switchMap, take, tap } from "rxjs/operators";

import { BetLimit } from "../../../data/casino-table/casino-table.model";
import { Game, HighlightedGame } from "../../../data/game/game.model";
import { GameCarouselStyleContext } from "../../../data/game-carousel/game-carousel.model";
import { Money } from "../../../data/money/money.model";
import { MultiGameLauncherPlayingType } from "../../../data/multi-game-launcher/multi-game-launcher.model";
import { GamingRoutes } from "../../../data/route";
import { GameRouteSelector } from "../../../data/router/game-route.selector";
import { GamingExternalState } from "../../../data/state";
import { GamingUiInteractionEvent } from "../../../data/telemetry/telemetry.model";
import { CasinoBlockService } from "../../casino-block/casino-block.service";
import { GameLauncherConfigService } from "../../game-launcher/game-launcher.config";
import { GamingTrackingContext, WidgetClickData } from "../../tracking/tracking.model";
import { GameThumbnailConfigService } from "../game-thumbnail.config";
import {
	GameThumbnailInteractionMode,
	GameThumbnailOptions,
	GameThumbnailUrls,
	ThumbnailClickData,
	TrackingType,
} from "../game-thumbnail.model";

import { GameThumbnailWrapperService } from "./game-thumbnail-wrapper.service";

@Component({
	// eslint-disable-next-line @angular-eslint/component-selector
	selector: "article[obg-m-game-thumbnail-wrapper]",
	templateUrl: "./game-thumbnail-wrapper.container.html",
	styleUrls: ["game-thumbnail-wrapper.container.scss"],
	changeDetection: ChangeDetectionStrategy.OnPush,
	encapsulation: ViewEncapsulation.None,
})
export class GameThumbnailWrapperContainer implements OnChanges, OnDestroy, OnInit {
	@Input() set options(options: GameThumbnailOptions) {
		this.defaultedOptions = { ...this.gameThumbnailConfig.getGameThumbnailOptions(), ...options };
	}

	@HostBinding("class.obg-m-game-thumbnail-wrapper") hostCssClass = true;
	@HostBinding("class.obg-m-game-thumbnail-error") @Input() error: string | undefined;
	/* xxsmall size will be removed when the new components will be used on the right sidebar. Since it’s not part of the design system */
	@HostBinding("class.xxsmall-thumbnail")
	get isXxsmall() {
		return this.thumbnailSize === "xxsmall";
	}

	@Input() jackpotMoney: Money | undefined;
	@Input() authenticated = false;
	@Input() game!: Game | HighlightedGame;
	@Input() collectionTrackingData: WidgetClickData | undefined;
	@Input() favorite = false;
	@Input() playTrackingData: Partial<UiInteraction> | undefined;
	@Input() interactionMode = GameThumbnailInteractionMode.Play;
	@Input() interactionModeIcon?: InteractionIcon = "ico-check";
	@Input() customActionText: string | undefined;
	@Input() styleOptions?: GameCarouselStyleContext;
	@Input() alternateStyle = false;
	@Input() thumbnailVariant?: ThumbnailVariant;
	@Input() thumbnailSize?: ThumbnailSize;
	@Input() showThumbnailHover? = true;
	@Input() sortOrder: number | undefined;
	@Input() @coerceBoolean() selectionMode = false;
	@Input() @coerceBoolean() selectionPaused = false;
	@Input() @coerceBoolean() multiGameWindowMode = false;
	@Input() @coerceBoolean() replaceUrl = false;
	@Input() @coerceBoolean() hideTitle = false;
	@Input() @coerceBoolean() censor = true;
	@Input() @coerceBoolean() showFavoriteIcon = true;
	@Input() @coerceBoolean() showTooltip = false;
	@Input() @coerceBoolean() alternateMultiGameWindowLink = false;
	@Input() @coerceBoolean() showErrorText = true;
	// Use responsive thumbnail approach only on mobile
	@Input() @coerceBoolean() fixedWidth = this.desktopConfigService.isDesktop();

	@Input() @coerceBoolean() showSubtitle = false;

	@Output() favoriteGame = new EventEmitter<Game>();
	@Output() playGame = new EventEmitter<Game>();
	@Output() selectedGame = new EventEmitter<boolean>();

	shouldCensor = false;
	hasGameTag = false;
	tooltipClass: string | undefined;

	readonly loginRoute: RouteLink = {
		route: CORE_ROUTE.auth.login,
	};

	readonly playForFunType = MultiGameLauncherPlayingType.PlayForFun;
	gameRoute: RouteLink | undefined;
	gameTag: string | undefined;
	betLimits: BetLimit | undefined;
	playForFunEnabled: boolean | undefined;
	isSelected = false;
	productBlocked = false;
	isPlayForRealTextButtonEnabled: boolean | undefined;
	isNavigateToGamePageEnabled: boolean | undefined;
	defaultedOptions = this.gameThumbnailConfig.getGameThumbnailOptions();

	jackpotValue: string | null = null;

	urls: GameThumbnailUrls = {
		baseUrl: "",
		loginUrl: "",
		loginAndPlayUrl: "",
		practicePlayUrl: "",
		returnUrl: "",
		returnLoginUrl: "",
	};

	translatedAriaLabels$ = this.gameThumbnailWrapperService.getTranslatedAriaLabels$();

	private gameRoute$: Observable<string | undefined> | undefined;
	private gameQueryParams$$: Subscription | undefined;
	private data$$: Subscription | undefined;

	constructor(
		private store: Store<GamingExternalState>,
		private gameRouteSelector: GameRouteSelector,
		private gameLauncherConfigService: GameLauncherConfigService,
		private changeDetectorRef: ChangeDetectorRef,
		private customerComplianceService: CustomerComplianceService,
		private casinoBlockService: CasinoBlockService,
		private gameThumbnailConfig: GameThumbnailConfigService,
		private currencyService: CurrencyService,
		private gameThumbnailWrapperService: GameThumbnailWrapperService,
		private desktopConfigService: DeviceConfigService,
		private uiInteractionAction: UiInteractionAction
	) {}

	ngOnInit() {
		const casinoBlock$ = this.casinoBlockService
			.checkCasinoBlocked$()
			.pipe(tap((casinoBlocked) => (this.productBlocked = casinoBlocked)));

		// todo: deprecate dutch logic when dutch jurisdiction is introduced in OBGB-10786
		const playForFunEnabled$ = this.customerComplianceService.getCurrentCompliance$().pipe(
			tap((currentCompliance) => {
				if (currentCompliance.type === "Dutch") {
					this.playForFunEnabled = false;
					return;
				}
				this.playForFunEnabled =
					this.gameLauncherConfigService.isCasinoPlayForFunAllowed(this.authenticated) &&
					!this.gameLauncherConfigService
						.getPrimaryVariantIdsWithDisabledPlayForFun()
						.includes(this.game!.primaryVariantId) &&
					!this.gameLauncherConfigService.getStudiosWithDisabledPlayForFun().includes(this.game!.provider) &&
					this.game!.isAvailableForFun;
			})
		);

		const shouldCensor$ = this.store.select(JurisdictionSelector.get).pipe(
			map((jurisdiction) => jurisdiction === Jurisdiction.Ukgc),
			tap((shouldCensor) => {
				this.shouldCensor = shouldCensor && !this.authenticated;
			})
		);

		this.data$$ = merge(casinoBlock$, playForFunEnabled$, shouldCensor$).subscribe(() =>
			this.changeDetectorRef.markForCheck()
		);

		this.isPlayForRealTextButtonEnabled = this.gameLauncherConfigService.isPlayForRealTextButtonEnabled();
	}

	ngOnChanges(changes: { game?: SimpleChange; favorite?: SimpleChange; authenticated?: SimpleChange }) {
		if (!this.game || !changes.game) {
			return;
		}

		this.tooltipClass = this.showTooltip ? "obg-m-game-thumbnail-simple-tooltip" : "";

		this.jackpotValue = this.getJackpotValue();

		this.isNavigateToGamePageEnabled = this.gameLauncherConfigService.isNavigateToGamePageEnabled();

		if (this.game.content.casinoTable) {
			this.betLimits = this.game.content.casinoTable.betLimits;
		}

		if (!isEmpty(this.game.tags)) {
			this.gameTag = this.game.tags![0];
			this.hasGameTag = !!this.game.tags![0];
		}

		if (this.gameQueryParams$$) {
			this.gameQueryParams$$.unsubscribe();
			this.gameQueryParams$$ = undefined;
		}

		this.gameRoute$ = this.store.pipe(
			select(this.gameRouteSelector.getRouteLinkByGameId(this.game.id, GamingRoutes.GameLauncher)),
			ignoreNil(),
			take(1),
			tap((route) => (this.gameRoute = route)),
			map((route) => route.route)
		);

		this.gameQueryParams$$ = this.gameRoute$
			.pipe(
				ignoreNil(),
				switchMap((route) =>
					this.gameThumbnailWrapperService.getUrls$(
						route,
						this.urls,
						this.gameRoute,
						this.loginRoute,
						this.urls.baseUrl,
						this.playForFunType,
						this.replaceUrl
					)
				),
				tap((urls) => {
					this.urls = { ...urls };
					this.changeDetectorRef.markForCheck();
				})
			)
			.subscribe();
	}

	ngOnDestroy() {
		this.gameQueryParams$$?.unsubscribe();
		this.data$$?.unsubscribe();
	}

	toggleFavorite() {
		this.favoriteGame.emit(this.game);
	}

	onThumbnailClicked({ detail: data }: CustomEvent<ThumbnailClickData>) {
		if (this.productBlocked) {
			this.casinoBlockService.openGameBlockDialog();
			return;
		}

		this.playGame.emit();
		if (!this.multiGameWindowMode) {
			this.navigateToRoute(data);
		}
	}

	navigateToRoute(data: ThumbnailClickData) {
		if (!this.gameRoute) {
			return;
		}

		const { urlType, trackingType } = data;
		const queryParams = this.gameThumbnailWrapperService.getQueryParams(
			urlType,
			this.loginRoute.route,
			this.urls.baseUrl,
			this.playForFunType
		);

		const { route, params } = queryParams?.useLoginRoute ? this.loginRoute : this.gameRoute;

		if (!route) {
			return;
		}

		const extras = {
			replaceUrl: this.replaceUrl,
			...queryParams,
		};

		if (trackingType) {
			this.trackGameSelected(trackingType);
		}

		setTimeout(() =>
			this.store.dispatch(RouteAction.navigate(route, params, { state: this.collectionTrackingData, ...extras }))
		);
	}

	onSelectGame() {
		if (!this.isSelected && this.selectionPaused) {
			return;
		}

		this.isSelected = !this.isSelected;
		this.selectedGame.emit(this.isSelected);
	}

	getJackpotValue() {
		if (this.jackpotMoney && this.game?.shouldShowJackpot) {
			return this.currencyService.format(
				this.jackpotMoney.value,
				undefined,
				this.jackpotMoney.currencyCode,
				"1.0-0"
			);
		}
		return "";
	}

	get isCensored(): boolean {
		return this.shouldCensor && this.censor;
	}

	private trackGameSelected(trackingType: TrackingType) {
		const eventType =
			trackingType === TrackingType.playForReal
				? GamingUiInteractionEvent.PlayReal
				: GamingUiInteractionEvent.PlayFun;

		this.store.dispatch(this.uiInteractionAction.emit(this.getPlayTrackingData(eventType)));
	}

	private getPlayTrackingData(event: GamingUiInteractionEvent): UiInteraction {
		const trackingData = {
			event,
			context: GamingTrackingContext.GameCategoryInteractionContext,
			data: {
				GameCategory: head(this.game!.gameTypes),
				GameName: this.game!.name,
				GameProvider: this.game!.provider,
				GameCollectionPosition: (this.sortOrder! + 1).toString(),
			},
		};
		return lodashMerge(trackingData, this.playTrackingData);
	}
}
